// Servicio de autenticación para integración con Authorizer
class AuthService {
    constructor() {
        this.baseURL = import.meta.env.VITE_AUTHORIZER_URL || 'https://demo.authorizer.dev'
        this.clientId = import.meta.env.VITE_AUTHORIZER_CLIENT_ID || 'demo-client-id'
        this.redirectURL = import.meta.env.VITE_AUTHORIZER_REDIRECT_URL || 'http://localhost:5173/auth/callback'
    }

    async login(email, password) {
        try {
            // Usuarios demo para desarrollo local con diferentes roles
            const demoUsers = {
                'demo@domus.com': {
                    password: 'demo123',
                    user: {
                        id: '1',
                        email: 'demo@domus.com',
                        name: 'Usuario Demo',
                        role: 'user',
                        group: 'default'
                    }
                },
                'admin@domus.com': {
                    password: 'admin123',
                    user: {
                        id: '2',
                        email: 'admin@domus.com',
                        name: 'Administrador Demo',
                        role: 'admin',
                        group: 'admins'
                    }
                },
                'superadmin@domus.com': {
                    password: 'super123',
                    user: {
                        id: '3',
                        email: 'superadmin@domus.com',
                        name: 'Superadministrador',
                        role: 'superadmin',
                        group: 'superadmins'
                    }
                },
                'pending@domus.com': {
                    password: 'pending123',
                    user: {
                        id: '4',
                        email: 'pending@domus.com',
                        name: 'Usuario Pendiente',
                        role: 'pending_approval',
                        group: 'pending'
                    }
                }
            }

            const demoUser = demoUsers[email]
            if (demoUser && demoUser.password === password) {
                const mockToken = this.generateMockJWT(demoUser.user)

                return {
                    user: demoUser.user,
                    token: mockToken,
                    expiresIn: 3600
                }
            }

            // Integración real con Authorizer
            const response = await fetch(`${this.baseURL}/graphql`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Authorizer-URL': this.baseURL,
                    'X-Authorizer-Client-ID': this.clientId
                },
                body: JSON.stringify({
                    query: `
                        mutation login($params: LoginInput!) {
                            login(params: $params) {
                                message
                                access_token
                                expires_in
                                user {
                                    id
                                    email
                                    given_name
                                    family_name
                                    email_verified
                                    roles
                                }
                            }
                        }
                    `,
                    variables: {
                        params: {
                            email,
                            password
                        }
                    }
                })
            })

            const result = await response.json()

            if (!response.ok || result.errors) {
                throw new Error(result.errors?.[0]?.message || 'Error al iniciar sesión')
            }

            const loginData = result.data.login

            return {
                user: {
                    id: loginData.user.id,
                    email: loginData.user.email,
                    name: `${loginData.user.given_name || ''} ${loginData.user.family_name || ''}`.trim() || loginData.user.email,
                    roles: loginData.user.roles || ['user'],
                    emailVerified: loginData.user.email_verified
                },
                token: loginData.access_token,
                expiresIn: loginData.expires_in
            }
        } catch (error) {
            console.error('Error en login:', error)
            throw error
        }
    }

    async register(email, password, name) {
        try {
            // Separar nombre y apellido si se proporciona
            const nameParts = (name || '').split(' ')
            const given_name = nameParts[0] || ''
            const family_name = nameParts.slice(1).join(' ') || ''

            const response = await fetch(`${this.baseURL}/graphql`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Authorizer-URL': this.baseURL,
                    'X-Authorizer-Client-ID': this.clientId
                },
                body: JSON.stringify({
                    query: `
                        mutation signup($params: SignUpInput!) {
                            signup(params: $params) {
                                message
                                access_token
                                expires_in
                                user {
                                    id
                                    email
                                    given_name
                                    family_name
                                    email_verified
                                    roles
                                }
                            }
                        }
                    `,
                    variables: {
                        params: {
                            email,
                            password,
                            confirm_password: password,
                            given_name,
                            family_name
                        }
                    }
                })
            })

            const result = await response.json()

            if (!response.ok || result.errors) {
                throw new Error(result.errors?.[0]?.message || 'Error al registrar usuario')
            }

            const signupData = result.data.signup

            return {
                user: {
                    id: signupData.user.id,
                    email: signupData.user.email,
                    name: `${signupData.user.given_name || ''} ${signupData.user.family_name || ''}`.trim() || signupData.user.email,
                    role: 'pending_approval', // Nuevos usuarios necesitan aprobación
                    group: 'pending',
                    emailVerified: signupData.user.email_verified
                },
                token: signupData.access_token,
                expiresIn: signupData.expires_in
            }
        } catch (error) {
            console.error('Error en registro:', error)
            throw error
        }
    }

    async refreshToken(token) {
        try {
            const response = await fetch(`${this.baseURL}/auth/refresh`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            })

            if (!response.ok) {
                throw new Error('Error al renovar token')
            }

            const data = await response.json()
            return data
        } catch (error) {
            console.error('Error al renovar token:', error)
            throw error
        }
    }

    verifyToken(token) {
        try {
            // Verificación básica del JWT (en producción usar una librería apropiada)
            const payload = this.parseJWT(token)

            if (payload.exp < Date.now() / 1000) {
                throw new Error('Token expirado')
            }

            return payload
        } catch (error) {
            throw new Error('Token inválido')
        }
    }

    logout() {
        // Limpiar cualquier estado local
        console.log('Usuario desconectado')
    }

    // Métodos auxiliares
    generateMockJWT(user) {
        const header = { alg: 'HS256', typ: 'JWT' }
        const payload = {
            ...user,
            exp: Math.floor(Date.now() / 1000) + 3600, // Expira en 1 hora
            iat: Math.floor(Date.now() / 1000)
        }

        // Mock JWT (no es seguro, solo para desarrollo)
        return `${btoa(JSON.stringify(header))}.${btoa(JSON.stringify(payload))}.mock-signature`
    }

    parseJWT(token) {
        try {
            const parts = token.split('.')
            if (parts.length !== 3) {
                throw new Error('JWT inválido')
            }

            const payload = JSON.parse(atob(parts[1]))
            return payload
        } catch (error) {
            throw new Error('Error al parsear JWT')
        }
    }

    getAuthHeaders(token) {
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    }
}

export const authService = new AuthService()